import { type ReactElement, useState } from 'react';
import { Badge, Button, EmptyState, Field, ListTable, Notice, PageHeader, TextInput, type Column, InlineStack, Text, InlineRow, Stack, Row } from '@vello/web-theme';
import { BI_PERSONAL_POLICY, grantsAllow, type SavedReport } from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useRouter } from '../router';
import { FormDialog } from '../components/form-dialog';
import { openInExplorer, useSavedReports } from '../components/bi/bi-reports-store';
import { ExportDialog, type ExportTarget } from '../components/bi/ExportDialog';
import { useCanExport } from '../components/bi/bi-exports-store';
import { BiProblem, useBiText } from '../components/bi/bi-ui';

/**
 * Análises › Relatórios salvos (D523; doc 20 §4.3): the operator's OWN saved explorer queries, kept on the server — the same list on
 * any browser. Open one (the explorer runs it again, inside the operator's access today), rename it, delete it (after a confirmation).
 * A report holds the QUERY, never its answer. A report that cuts by a person is opened only by a holder of
 * `analytics:view_personal`; whoever holds `analytics:save_report` may rename and delete (the list is read with `analytics:view`).
 * What D522 kept in this browser is moved to the account the first time the list opens, and the screen says how it went.
 */
export function AnalyticsReportsScreen(): ReactElement {
  const { t, lang } = useT();
  const { v } = useVocab();
  const text = useBiText();
  const { grants } = useAdmin();
  const { navigate } = useRouter();
  const { confirm, toast } = useConsole();
  const store = useSavedReports();
  const canExport = useCanExport();
  const [background, setBackground] = useState<ExportTarget | undefined>(undefined);
  const [renaming, setRenaming] = useState<SavedReport | undefined>(undefined);
  const [name, setName] = useState('');
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const when = new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short' });
  const personalWord = grants !== undefined && grantsAllow(grants, BI_PERSONAL_POLICY);
  const ready = store.state.status === 'ready';
  const rows = store.state.status === 'ready' ? store.state.value : [];

  const summary = (r: SavedReport): string => {
    const measures = r.query.metrics.map(text.metricName).join(', ');
    const grain = r.query.granularity !== undefined && r.query.granularity !== 'total' ? [v('analytics.granularity', r.query.granularity)] : [];
    const cuts = [...grain, ...(r.query.dimensions ?? []).map(text.dimName)].join(', ');
    const period = r.state === undefined ? '' : r.state.period.kind === 'preset' ? v('analytics.relative_period', r.state.period.preset) : r.state.period.kind === 'lastDays' ? t('bi.period.lastDaysText', { n: r.state.period.days }) : `${r.state.period.from} → ${r.state.period.to}`;
    return [measures, cuts !== '' ? t('bi.reports.by', { cuts }) : '', period].filter((x) => x !== '').join(' · ');
  };

  const open = (r: SavedReport): void => {
    if (r.state === undefined) return;
    openInExplorer(r.state);
    navigate('bi_explorer');
  };
  const startRename = (r: SavedReport): void => {
    setRenaming(r);
    setName(r.name);
    setProblem(undefined);
  };
  const submitRename = async (): Promise<void> => {
    if (renaming === undefined) return;
    setBusy(true);
    const res = await store.rename(renaming.id, name);
    setBusy(false);
    if (!res.ok) {
      setProblem(t(res.error.messageId));
      return;
    }
    setRenaming(undefined);
    toast({ tone: 'success', message: t('bi.reports.renamed', { name: res.value.name }) });
  };
  const askDelete = async (r: SavedReport): Promise<void> => {
    const answer = await confirm({ title: t('bi.reports.delete.title', { name: r.name }), body: t('bi.reports.delete.body'), confirmLabel: t('bi.reports.delete.confirm'), cancelLabel: t('ui.cancel'), tone: 'danger' });
    if (!answer.confirmed) return;
    const res = await store.remove(r.id);
    toast(res.ok ? { tone: 'success', message: t('bi.reports.deleted', { name: r.name }) } : { tone: 'warning', message: t(res.error.messageId) });
  };

  const columns: readonly Column<SavedReport>[] = [
    {
      key: 'name',
      header: t('bi.reports.col.name'),
      width: '1.4fr',
      render: (r) => (
        <InlineStack gap={1}>
          <strong>{r.name}</strong>
          <Text role="caption" tone="secondary">{summary(r)}</Text>
          {r.state === undefined && <Text role="caption" tone="secondary">{t('bi.reports.unopenable')}</Text>}
          {r.personal && !personalWord && <Text role="caption" tone="secondary">{t('bi.reports.needsPeople')}</Text>}
        </InlineStack>
      ),
    },
    {
      key: 'kind',
      header: t('bi.reports.col.kind'),
      width: 'max-content',
      render: (r) => (
        <InlineRow gap={2} align="stretch" wrap>
          <Badge>{t(r.state?.mode === 'ranking' ? 'bi.mode.ranking' : 'bi.mode.table')}</Badge>
          {r.personal && <Badge tone="warning">{t('bi.personal.tag')}</Badge>}
        </InlineRow>
      ),
    },
    { key: 'updated', header: t('bi.reports.col.updated'), width: '0.8fr', render: (r) => <span className="tnum">{when.format(new Date(r.updatedAt))}</span> },
  ];

  const imported = store.imported;
  return (
    <>
      <PageHeader title={t('bi.reports.title')} subtitle={t('bi.reports.subtitle')} />
      <Stack gap={3}>
        <Notice tone="neutral">{t('bi.reports.where')}</Notice>
        {ready && !store.canSave && <Notice tone="neutral">{t('bi.reports.readOnly')}</Notice>}
        {imported !== undefined && imported.imported + imported.alreadyThere > 0 && (
          <Notice tone="success">{t('bi.reports.imported', { count: imported.imported + imported.alreadyThere })}</Notice>
        )}
        {imported !== undefined && imported.refused > 0 && <Notice tone="warning">{t('bi.reports.importRefused', { count: imported.refused })}</Notice>}
        {imported !== undefined && imported.found > 0 && !imported.cleared && <Notice tone="warning">{t('bi.reports.importLater')}</Notice>}
        {store.state.status === 'error' && <BiProblem error={store.state.error} onRetry={store.reload} text={text} />}
      </Stack>
      <ListTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={store.state.status === 'loading'}
        loadingLabel={t('ui.loading')}
        actions={(r) => (
          <>
            <Button size="sm" variant="secondary" disabled={r.state === undefined || (r.personal && !personalWord)} onClick={() => open(r)}>
              {t('ui.open')}
            </Button>
            {canExport && (
              <Button size="sm" variant="ghost" icon="download" disabled={r.state === undefined || (r.personal && !personalWord)} onClick={() => setBackground({ source: { kind: 'SAVED_REPORT', reportId: r.id }, title: r.name })}>
                {t('bi.exportBackground')}
              </Button>
            )}
            {store.canSave && (
              <>
                <Button size="sm" variant="ghost" icon="pencil" onClick={() => startRename(r)}>
                  {t('bi.reports.rename')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => void askDelete(r)}>
                  {t('bi.reports.delete')}
                </Button>
              </>
            )}
          </>
        )}
        actionsWidth="max-content"
        empty={store.state.status === 'error' ? undefined : <EmptyState title={t('bi.reports.empty')} detail={t('bi.reports.empty.hint')} />}
        minWidth={640}
      />

      <ExportDialog open={background !== undefined} target={background} onClose={() => setBackground(undefined)} />

      <FormDialog open={renaming !== undefined} id="bi-rename" icon="pencil" title={t('bi.reports.rename.title')} body={t('bi.reports.rename.body')} busy={busy} initialFocusId="bi-rename-name" onCancel={() => setRenaming(undefined)}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submitRename();
          }}
          noValidate
        >
          <Field label={t('bi.reports.name')} htmlFor="bi-rename-name" {...(problem !== undefined ? { error: problem } : {})}>
            <TextInput id="bi-rename-name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Row gap={2} align="stretch">
            <Button className="vello-admin-a-btn-grow" variant="ghost" onClick={() => setRenaming(undefined)}>
              {t('ui.cancel')}
            </Button>
            <Button className="vello-admin-a-btn-grow" type="submit" variant="primary" loading={busy}>
              {t('bi.reports.rename.confirm')}
            </Button>
          </Row>
        </form>
      </FormDialog>
    </>
  );
}
